import { NgModule, APP_INITIALIZER, provideZoneChangeDetection } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MatToolbarModule } from '@angular/material/toolbar'
import { MatSidenavModule } from '@angular/material/sidenav'
import { MatListModule } from '@angular/material/list'
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { DashboardComponent } from './dashboard/dashboard.component'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MatStepperModule } from '@angular/material/stepper';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { PendingChangesGuard } from './common/services/pending-changes-guard.service';
import { MatDialogModule } from '@angular/material/dialog';
import { GlossaryComponent } from './glossary/glossary.component';
import { OverviewComponent } from './overview/overview.component';
import { SnackbarComponent } from './snackbar/snackbar.component';
import { ConfirmationDialogComponent } from './dialogs/dialog-unsaved-changes/confirmation-dialog.component';
import { LayoutModule } from '@angular/cdk/layout';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DialogSubmitPlanComponent } from './dialogs/dialog-submit-plan/dialog-submit-plan.component';
import { ViewPlanComponent } from './view-plan/view-plan.component';
import { TrackProgressComponent } from './track-progress/track-progress.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TextFieldModule } from '@angular/cdk/text-field';
import { PlanGuard } from './common/services/plan-guard.service';
import { LayoutRoutingModule } from './layout/layout-routing.module';
import { NoDblClickDirectiveModule } from './common/directives/no-dbl-click.directive.module';
import { DialogLearnMoreComponent } from './dialogs/dialog-learn-more/dialog-learn-more.component';
import { DialogExamplePlanComponent } from './dialogs/dialog-exampleplan/dialog-exampleplan.component';
import { PrivacyComponent } from './privacy/privacy.component';
import { MatMenuModule } from '@angular/material/menu';
import { ResponseInterceptor } from './common/interceptors/response-interceptor.service';
import { ProfileComponent } from './profile/profile.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContactUsComponent } from './contact-us/contact-us.component';
import { AccessibilityComponent } from './accessibility/accessibility.component';
import { RegexDirectiveModule } from './common/directives/regex.directive.module';
import { StarRatingComponent } from './star-rating/star-rating.component';
import { MatTabsModule } from '@angular/material/tabs';
import { DialogConfirmProfileComponent } from './dialogs/dialog-confirm-profile/dialog-confirm-profile.component';
import { LearningResourcesComponent } from './learning-resources/learning-resources.component';
import { LearningResourcesFilterPipe } from './common/pipes/learning-resources-filter.pipe';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { LearningResourcesSortPipe } from './common/pipes/learning-resources-sort.pipe';
import { LearningResourcesSearchPipe } from './common/pipes/learning-resources-search.pipe';
import { LearningResourceGuard } from './common/guards/learning-resource.guard';
import { MatPaginatorModule } from '@angular/material/paginator';
import { PaginationFilterPipe } from './common/pipes/pagination.pipe';
import { ExpandableChipsComponent } from './components/collapsible-chips/expandable-chips.component';
import { CollapsiblePanelComponent } from './components/collapsible-panel/collapsible-panel.component';
import { MatTooltipComponent } from './components/tooltip/tooltip.component';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule} from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { InlineSpinnerComponent } from './components/inline-spinner/inline-spinner.component';
import { DialogLoadingComponent } from './dialogs/dialog-loading/dialog-loading.component';
import {
  MsalModule,
  MsalInterceptor,
  MsalGuard,
  MsalService,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation
} from '@azure/msal-browser';
import { environment } from 'src/environments/environment';
import { UserService } from './common/services/user.service';
import { StartupService } from './common/services/startup.service';
import { PlanService } from './common/services/plan.service';
import { SharedService } from './common/services/shared.service';
import { SessionDialogComponent } from './dialogs/dialog-session/session-dialog.component';

// Create the MSAL instance
const msalInstance = new PublicClientApplication({
  auth: {
      clientId: environment.MSALClientID,
      authority: environment.MSALAuthority,
      redirectUri: environment.MSALRedirectUri,
      navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage
  }
});

export function MSALInstanceFactory(): IPublicClientApplication {
  return msalInstance;
}

export function MSALInterceptorConfigFactory() {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.ApiEndpoint, [environment.MSALApi]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [environment.MSALApi]
    }
  };
}

// Initialize MSAL before anything else
export function initializeMsal(startupService: StartupService) {
  return () => startupService.init(); // must return a function; may return Promise<void>
}

@NgModule({ declarations: [
        AppComponent,
        DashboardComponent,
        ConfirmationDialogComponent,
        GlossaryComponent,
        OverviewComponent,
        SnackbarComponent,
        DialogSubmitPlanComponent,
        ViewPlanComponent,
        TrackProgressComponent,
        DialogLearnMoreComponent,
        DialogExamplePlanComponent,
        PrivacyComponent,
        ProfileComponent,
        ContactUsComponent,
        AccessibilityComponent,
        StarRatingComponent,
        DialogConfirmProfileComponent,
        LearningResourcesComponent,
        LearningResourcesFilterPipe,
        LearningResourcesSortPipe,
        LearningResourcesSearchPipe,
        PaginationFilterPipe,
        ExpandableChipsComponent,
        CollapsiblePanelComponent,
        MatTooltipComponent,
        InlineSpinnerComponent,
        DialogLoadingComponent,
        SessionDialogComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        MatToolbarModule,
        MatSidenavModule,
        MatListModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        MatGridListModule,
        MatStepperModule,
        ReactiveFormsModule,
        FormsModule,
        MatInputModule,
        MatSelectModule,
        MatChipsModule,
        MatGridListModule,
        MatSnackBarModule,
        MatDialogModule,
        LayoutModule,
        MatProgressSpinnerModule,
        MatButtonModule,
        MatProgressBarModule,
        TextFieldModule,
        LayoutRoutingModule,
        NoDblClickDirectiveModule,
        MatMenuModule,
        MatIconModule,
        MatTooltipModule,
        RegexDirectiveModule,
        MatTabsModule,
        MatExpansionModule,
        MatCheckboxModule,
        MatPaginatorModule,
        MatRadioModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MsalModule,
        AppRoutingModule], providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ResponseInterceptor,
            multi: true
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeMsal,
            deps: [StartupService, MsalService, UserService, PlanService, SharedService],
            multi: true
        },
        provideZoneChangeDetection({ eventCoalescing: true }),
        PendingChangesGuard,
        PlanGuard,
        LearningResourceGuard,
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        UserService,
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
